import {
  ChangeEvent,
  ChangeEventHandler,
  Dispatch,
  FocusEventHandler,
  forwardRef,
  HTMLProps,
  KeyboardEvent,
  memo,
  Ref,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { differenceInDays, format } from 'date-fns'
import { KeyboardType } from 'grommet/utils'
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil'
import styled from 'styled-components'
import {
  autoUpdate,
  offset,
  ReferenceType,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole
} from '@floating-ui/react'
import { mergeRefs } from 'use-callback-ref'

import {
  Avatar,
  AvatarGroup,
  AvatarSubject,
  Box,
  durationParser,
  duration as formatDuration,
  TaskItemIcon,
  TaskItemLayout,
  Text,
  TextInput,
  TextInputProps,
  themeColor,
  Tooltip,
  useOnScreen,
  useUpdateEffect
} from '@cutover/react-ui'
import { TaskTypeIconName } from '@cutover/icons'
import {
  accountTaskTypeLookup,
  taskCreateNameState,
  useCanCreateRootTask,
  useNewTaskStreamId,
  useRunbookId,
  useRunbookVersionId,
  useRunbookVersionProperty,
  useSetTaskCreateState,
  useStream,
  useTaskListTask,
  useTaskTypeOrDefault,
  useToggleTaskCreateState
} from 'main/recoil/runbook'
import { useGlobalTaskLevelOrDefault } from 'main/recoil/shared/global-config'
import { useLanguage } from 'main/services/hooks'
import {
  RunbookTimingMode,
  RunbookVersion,
  StreamListStream,
  TaskListTask,
  TaskType
} from 'main/services/queries/types'
import { createTask, TaskCreatePayload } from 'main/services/queries/use-task'
import { TaskSuccessorsModal } from 'main/components/runbook/modals/task-modals/task-successors-modal'
import { useProcessTaskCreateResponse } from 'main/recoil/runbook/updaters/task-operations'
import { RunbookTaskCreateResponse } from 'main/services/api/data-providers/runbook-types'
import {
  TaskShortcutMenuItem,
  useBuildStreamsMenuItems,
  useBuildTaskTypeMenuItems,
  useBuildUsersAndTeamsMenuItems
} from './use-task-list-item-menu-builder'
import { TaskItemCreateMenu } from './task-item-create-menu'

/**
 * TaskItemCreateToggle toggle for showing/hiding TaskItemCreate
 */
export const TaskItemCreateToggle = memo(({ predecessorId }: { predecessorId: number }) => {
  const [currentPredecessor, toggleCreateInput] = useToggleTaskCreateState()

  const handleToggle = useCallback(() => {
    toggleCreateInput({ fromPredecessorId: predecessorId })
  }, [predecessorId])

  return predecessorId === currentPredecessor ? <TaskItemCreate onCancel={handleToggle} /> : null
})

export const useRootTaskCreateForm = ({ taskCount }: { taskCount: number }) => {
  const canCreateRootTask = useCanCreateRootTask()
  const newTaskStreamId = useNewTaskStreamId()
  const setCreateInput = useSetTaskCreateState()
  const [currentPredecessor] = useToggleTaskCreateState()

  const showRootTaskForm = canCreateRootTask && currentPredecessor === 0

  // Note: currentPredecessor stores the state of the current new task form. '0' is for the root task input
  // If there are no results and the user has permission, open it by default (set to 0)
  useEffect(() => {
    if (currentPredecessor && currentPredecessor > 0) return
    const rootTaskFormAutoOpen = taskCount === 0 && canCreateRootTask
    setCreateInput({ fromPredecessorId: rootTaskFormAutoOpen ? 0 : undefined })
  }, [taskCount, canCreateRootTask, newTaskStreamId])

  return showRootTaskForm ? <TaskItemCreateToggle predecessorId={0} /> : null
}

export type NewTaskInputProps = TextInputProps & {
  onCancel?: any
  onCreate?: () => void
  taskTypes?: TaskType[]
  streams?: StreamListStream[]
  streamLookup?: Record<number, StreamListStream>
  runbookTimingMode?: RunbookVersion['timing_mode']
  runbookStartPlanned?: RunbookVersion['start_planned']
  canAdd?: boolean
}

type MenuType = 'taskTypes' | 'usersAndTeams' | 'streams'

type NewTaskState = Omit<TaskCreatePayload['task'], 'stream_id' | 'task_type_id'>

const getInitialDate = (endDisplay?: number, startPlanned?: number | null, timingMode?: RunbookTimingMode) => {
  if (timingMode === 'scheduled')
    return format((endDisplay ? endDisplay : startPlanned ? startPlanned : 0) * 1000, 'd LLL HH:mm')

  const date = new Date((endDisplay || startPlanned || 0) * 1000)
  const dayZero = new Date((startPlanned || 0) * 1000)
  const days = differenceInDays(dayZero, date) + 1
  const time = format(date, 'HH:mm')

  return `Day ${days} ${time}`
}

/**
 * TaskItemCreate w/ data from recoil state
 */
export const TaskItemCreate = memo(({ onCancel }: NewTaskInputProps) => {
  const buildTaskTypeMenuItems = useBuildTaskTypeMenuItems()
  const buildUsersAndTeamsMenuItems = useBuildUsersAndTeamsMenuItems()
  const buildStreamsMenuItems = useBuildStreamsMenuItems()
  const [isMenuOpen, setMenuOpen] = useState(false)
  const [menuItems, setMenuItems] = useState<TaskShortcutMenuItem[] | undefined>(undefined)
  const [filteredMenuItems, setFilteredMenuItems] = useState<TaskShortcutMenuItem[] | undefined>(undefined)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const { refs, floatingStyles, context } = useFloating({
    open: isMenuOpen,
    onOpenChange: setMenuOpen,
    whileElementsMounted: autoUpdate,
    placement: 'top-start',
    middleware: [offset(6)]
  })
  const listRef = useRef<Array<HTMLElement | null>>([])
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true
  })
  const role = useRole(context, { role: 'listbox' })
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([role, dismiss, listNavigation])
  const [menuType, setMenuType] = useState<MenuType | undefined>(undefined)
  const [afterSelectionSubstring, setAfterSelectionSubstring] = useState<string | undefined>(undefined)
  const inputRef = useRef<HTMLInputElement>(null)
  const isInputVisible = useOnScreen(inputRef)
  const [isSuccessorsModalOpen, setSuccessorsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [createTaskPredecessor, toggleCreateTaskInput] = useToggleTaskCreateState()
  const runbookVersionId = useRunbookVersionId()
  const runbookId = useRunbookId()
  const runbookTimingMode = useRunbookVersionProperty({ attribute: 'timing_mode' })
  const runbookStartPlanned = useRunbookVersionProperty({ attribute: 'start_planned' })
  const previousTask = useTaskListTask(createTaskPredecessor || 0)
  const processTaskCreateResponse = useProcessTaskCreateResponse()
  const previousTaskType = useTaskTypeOrDefault(previousTask?.task_type_id)
  const newTaskStreamId = useNewTaskStreamId({ prevTaskStreamId: previousTask?.stream_id })
  const newTaskStream = useStream(newTaskStreamId || 0)
  const parentStream = useStream(newTaskStream?.parent_id || 0)
  const initialTaskType = useTaskTypeOrDefault(
    newTaskStream?.default_task_type_id || parentStream?.default_task_type_id
  )
  const taskTypeLookup = useRecoilValue(accountTaskTypeLookup)
  const initialLevel = useGlobalTaskLevelOrDefault(previousTask?.level)
  const initialDuration = useMemo(
    () => (initialTaskType?.auto_finish ? 0 : initialTaskType?.default_duration),
    [initialTaskType?.auto_finish, initialTaskType?.default_duration]
  )
  // TODO make date take account of timezones https://cutover.atlassian.net/browse/CFE-1400
  const dateText = useMemo(
    () => getInitialDate(previousTask?.end_display, runbookStartPlanned, runbookTimingMode),
    [previousTask?.end_display, runbookStartPlanned, runbookTimingMode]
  )

  const [dummy, setDummy] = useState<(NewTaskState & { dateText: string; previousTask?: TaskListTask }) | undefined>(
    undefined
  )
  const [tempTaskName, setTempTaskName] = useRecoilState(taskCreateNameState)
  const [newTaskData, setNewTaskData] = useState<NewTaskState>({
    disable_notify: false,
    end_fixed: null,
    stream: newTaskStream,
    level: initialLevel.id,
    level_name: initialLevel.name,
    task_type: initialTaskType,
    name: tempTaskName ?? '',
    stage: 'new',
    duration: initialDuration
  })
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [selectedTeams, setSelectedTeams] = useState<number[]>([])
  const [avatarSubjects, setAvatarSubjects] = useState<AvatarSubject[]>([])

  useUpdateEffect(() => {
    // Update the stream when the url changes
    updateNewTaskData({ stream: newTaskStream })
  }, [newTaskStream])

  const updateNewTaskData = (data: Partial<NewTaskState>) => {
    setNewTaskData(prev => ({ ...prev, ...data }))
  }

  const onClose = () => {
    setTempTaskName(undefined)
    onCancel?.()
  }

  const handleBlur: FocusEventHandler<HTMLInputElement> = () => {
    if (isLoading || dummy || newTaskData.name.length > 0 || createTaskPredecessor === 0) return
    resetMenu()
    onClose()
  }

  /**
   * Handles the filtering logic based on user input for task names.
   * Matches the last occurrence of special characters ('^', '@', or '$') triggering menu options,
   * filters menu items accordingly, and sets the menu state based on the input.
   *
   * @param {string} tempTaskName - The temporary task name input by the user.
   */
  useUpdateEffect(() => {
    if (tempTaskName && tempTaskName.length > 0) {
      const regex = /[\^@$][^[\^@$]*$/
      const match = tempTaskName.match(regex)

      if (match) {
        const matchedSubstring = match[0].substring(1).toLowerCase()
        const remainingSubstring = tempTaskName.replace(match[0], '')
        setAfterSelectionSubstring(remainingSubstring.trim())

        const filteredItems = (menuItems ?? []).filter(item => item.label.toLowerCase().includes(matchedSubstring))
        setFilteredMenuItems(filteredItems)
        setMenuOpen(true)
      } else {
        resetMenu()
      }
    } else {
      resetMenu()
    }
  }, [tempTaskName])

  const resetNewTask = useCallback(() => {
    setNewTaskData({
      disable_notify: false,
      end_fixed: null,
      stage: 'new',
      stream: newTaskStream,
      level: initialLevel.id,
      level_name: initialLevel.name,
      task_type: initialTaskType,
      name: '',
      duration: initialDuration
    })
  }, [newTaskStream, initialLevel, initialTaskType])

  const handleKeyDown: KeyboardType = event => {
    event.stopPropagation()
    const input = event.target as HTMLInputElement
    const inputValue = input?.value

    switch (event.key) {
      case 'Enter':
        return handleTaskCreate()
      case 'Escape':
        setTempTaskName(undefined)
        return toggleCreateTaskInput({ fromPredecessorId: createTaskPredecessor })
      case '$':
        return matchStreams()
      case '@':
        return matchUsersAndTeams()
      case '^':
        return matchTaskTypes()
      case ' ':
        matchDuration(inputValue)
    }
  }

  const matchStreams = useRecoilCallback(() => async () => {
    const streams = await buildStreamsMenuItems()
    setupAndOpenMenu(streams, 'streams')
  })

  const matchUsersAndTeams = useRecoilCallback(() => async () => {
    const usersAndTeams = await buildUsersAndTeamsMenuItems()
    const filterSelectedUsersAndTeams = usersAndTeams?.filter((item: TaskShortcutMenuItem) => {
      return item.props?.team?.team_id ? !selectedTeams.includes(item.id) : !selectedUsers.includes(item.id)
    })
    setupAndOpenMenu(filterSelectedUsersAndTeams, 'usersAndTeams')
  })

  const matchTaskTypes = useRecoilCallback(() => async () => {
    const taskTypes = await buildTaskTypeMenuItems()
    setupAndOpenMenu(taskTypes, 'taskTypes')
  })

  const matchDuration = (inputValue: string) => {
    const matchPattern = new RegExp(/\b(\d+[dhms])+?\b/)
    const words = inputValue.split(/\s+/)

    words.forEach(word => {
      if (matchPattern.test(word)) {
        // word is a duration string, eg 45h30m, convert it to an actual duration
        const durationSeconds = durationParser(word)
        // now, remove the matching string from the task title (plus the space before it)
        const textToRemove = new RegExp(`\\s*${word}`, 'g')
        const newName = newTaskData.name.replace(textToRemove, '')
        updateNewTaskData({ name: newName, duration: durationSeconds })
        setAfterSelectionSubstring(newName)
        resetMenu()
      }
    })
  }

  const setupAndOpenMenu = (items: TaskShortcutMenuItem[], type: MenuType) => {
    setMenuType(type)
    setMenuItems(items)
    setFilteredMenuItems(items)
    setMenuOpen(true)
  }

  const handleMenuSelection = ({ activeIndex, item }: { activeIndex?: number | null; item?: TaskShortcutMenuItem }) => {
    if (!filteredMenuItems) return

    let selectedItem
    if (activeIndex !== null && activeIndex !== undefined) {
      selectedItem = filteredMenuItems[activeIndex]
    } else if (item) {
      selectedItem = item
    }

    if (!selectedItem) return

    let taskData: NewTaskState = { ...newTaskData }
    switch (menuType) {
      case 'taskTypes':
        const taskType = taskTypeLookup[selectedItem.id]
        taskData.task_type = taskType
        taskData.duration = taskType.auto_finish ? 0 : taskType.default_duration
        break
      case 'usersAndTeams':
        if (selectedItem.props?.team?.team_id) {
          setSelectedTeams([...selectedTeams, selectedItem.id])
        } else {
          setSelectedUsers([...selectedUsers, selectedItem.id])
        }
        setAvatarSubjects([...avatarSubjects, selectedItem.props.avatarSubject])
        break
      case 'streams':
        taskData.stream = selectedItem.props?.stream
        break
      default:
        break
    }
    setNewTaskData(taskData)
    resetMenu()
  }

  const resetMenu = () => {
    setMenuOpen(false)
    setMenuItems(undefined)
    setFilteredMenuItems(undefined)
    setMenuType(undefined)

    if (afterSelectionSubstring !== undefined) {
      setTempTaskName(afterSelectionSubstring)
      updateNewTaskData({ name: afterSelectionSubstring })
      setAfterSelectionSubstring(undefined)
    }
  }

  useUpdateEffect(() => {
    if (!isInputVisible) {
      resetMenu()
    }
  }, [isInputVisible])

  const handleChangeContent: ChangeEventHandler<HTMLInputElement> = event => {
    setTempTaskName(event.target.value)
    updateNewTaskData({ name: event.target.value })
  }

  const handleSuccessorSelection = (successors: number[]) => {
    setSuccessorsModalOpen(false)
    handleMutation(successors)
  }

  const handleTaskCreate = () => {
    if (isLoading || dummy || !newTaskData.name) return

    if (previousTask?.successor_ids && previousTask.successor_ids.length > 1) {
      setSuccessorsModalOpen(true)
    } else {
      handleMutation(previousTask ? [...previousTask.successor_ids] : [])
    }
  }

  const handleMutation = async (successors: number[]) => {
    setIsLoading(true)
    const payload = {
      task: { ...newTaskData, stream_id: newTaskData.stream?.id, task_type_id: newTaskData.task_type.id },
      base_task_id: createTaskPredecessor || null,
      predecessors: previousTask ? [previousTask.id] : [],
      successors,
      runbook_teams: selectedTeams,
      users: selectedUsers,
      integration_events: []
    }

    try {
      setDummy({ ...newTaskData, dateText, previousTask })
      resetNewTask()
      setTempTaskName(undefined)

      const response = await createTask({ runbookId, runbookVersionId, payload })
      processTaskCreateResponse(response as RunbookTaskCreateResponse)
      toggleCreateTaskInput({ fromPredecessorId: response.task.id })
      setIsLoading(false)
    } catch {
      setDummy(undefined)
      resetNewTask()
      setTempTaskName(undefined)
      setIsLoading(false)
    }
  }

  useUpdateEffect(() => {
    if (dummy) {
      inputRef.current?.focus()
    }
  }, [dummy])

  return (
    <>
      {dummy && (
        <TaskItemCreateInput
          dummy
          disabled
          date={dummy.dateText}
          previousTask={dummy.previousTask}
          previousTaskTypeIcon={previousTaskType?.icon as TaskTypeIconName}
          streamColor={dummy.stream?.color}
          taskName={dummy.name}
          taskTypeIcon={(dummy.task_type.icon + '-dashed') as TaskTypeIconName}
          duration={dummy.duration}
        />
      )}
      <TaskItemCreateInput
        ref={inputRef}
        setReference={refs.setReference}
        getReferenceProps={getReferenceProps}
        activeIndex={activeIndex}
        setMenuOpen={setMenuOpen}
        setActiveIndex={setActiveIndex}
        onSelectMenu={handleMenuSelection}
        previousTask={previousTask}
        previousTaskTypeIcon={previousTaskType?.icon as TaskTypeIconName}
        date={dateText}
        streamColor={newTaskData.stream?.color}
        streamName={newTaskData.stream?.name}
        taskName={newTaskData.name}
        taskTypeIcon={(newTaskData.task_type.icon + '-dashed') as TaskTypeIconName}
        levelName={newTaskData.level_name}
        duration={newTaskData.duration}
        handleChangeContent={handleChangeContent}
        handleKeyDown={handleKeyDown}
        onCancel={onClose}
        onBlur={handleBlur}
        avatarSubjects={avatarSubjects}
      />
      {!dummy && isMenuOpen && (
        <TaskItemCreateMenu
          isMenuOpen={isMenuOpen}
          menuItems={filteredMenuItems}
          getFloatingProps={getFloatingProps}
          getItemProps={getItemProps}
          context={context}
          setFloating={refs.setFloating}
          floatingStyles={floatingStyles}
          listRef={listRef}
          activeIndex={activeIndex}
          onSelectMenu={handleMenuSelection}
          constraints={{ minWidth: '300px', maxHeight: '200px' }}
        />
      )}
      {previousTask && (
        <TaskSuccessorsModal
          open={isSuccessorsModalOpen}
          onClose={() => setSuccessorsModalOpen(false)}
          previousTask={previousTask}
          onSubmit={(successors: number[]) => handleSuccessorSelection(successors)}
        />
      )}
    </>
  )
})

/**
 * TaskItemCreateInput presentational only
 */
const TaskItemCreateInput = forwardRef(
  (
    {
      dummy = false,
      previousTask,
      date,
      disabled,
      streamColor,
      taskTypeIcon,
      previousTaskTypeIcon,
      onBlur,
      onCancel,
      handleChangeContent,
      handleKeyDown,
      taskName,
      streamName,
      levelName,
      duration,
      setReference,
      getReferenceProps,
      activeIndex,
      setMenuOpen,
      setActiveIndex,
      onSelectMenu,
      avatarSubjects
    }: {
      dummy?: boolean
      previousTask?: TaskListTask
      date: string | number
      disabled?: boolean
      streamColor: string
      taskTypeIcon: TaskTypeIconName
      previousTaskTypeIcon?: TaskTypeIconName
      onCancel?: () => void
      onBlur?: FocusEventHandler<HTMLInputElement>
      handleChangeContent?: (e: ChangeEvent<HTMLInputElement>) => void
      handleKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
      taskName: string
      streamName?: string
      levelName?: string
      duration?: number
      setReference?: ((node: ReferenceType | null) => void) & ((node: ReferenceType | null) => void)
      getReferenceProps?: (userProps?: HTMLProps<Element> | undefined) => Record<string, unknown>
      activeIndex?: number | null
      setMenuOpen?: Dispatch<SetStateAction<boolean>>
      setActiveIndex?: Dispatch<SetStateAction<number | null>>
      onSelectMenu?: ({ activeIndex, item }: { activeIndex?: number; item?: TaskShortcutMenuItem }) => void
      avatarSubjects?: AvatarSubject[]
    },
    ref: Ref<HTMLInputElement>
  ) => {
    const { t } = useLanguage('runbook', { keyPrefix: 'taskList' })
    const tempTaskName = useRecoilValue(taskCreateNameState)
    const [value, setValue] = useState(dummy ? taskName : tempTaskName ?? '')

    useEffect(() => {
      if (dummy) {
        setValue(taskName)
      } else {
        setValue(tempTaskName ?? '')
      }
    }, [dummy, taskName, tempTaskName])

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (!dummy) {
        handleChangeContent?.(e)
        setValue(e.target.value)
      }
    }

    const inputProps = dummy
      ? { value: taskName }
      : {
          ...getReferenceProps?.({
            ref: mergeRefs([ref, setReference ?? null]),
            onChange: handleChange,
            value,
            placeholder: t('enterTitle'),
            'aria-autocomplete': 'list',
            onKeyDown(e) {
              if (e.key === 'Enter' && activeIndex != null) {
                setActiveIndex?.(null)
                setMenuOpen?.(false)
                onSelectMenu?.({ activeIndex })
              } else {
                handleKeyDown?.(e as KeyboardEvent<HTMLInputElement>)
              }
            }
          })
        }

    return (
      <TaskItemLayout
        disabled={disabled}
        data-testid={`task-list-item-create-from-${previousTask?.id ? previousTask?.id : 'root'}`}
        dateContent={<Text color="text-light">{date}</Text>}
        icon={
          <TaskItemIcon
            color={streamColor}
            icon={taskTypeIcon}
            disabled={disabled}
            onClick={onCancel}
            hoverIcon="close"
            withConnectors={previousTask?.id ? { previousIcon: previousTaskTypeIcon } : undefined}
          />
        }
        mainContent={
          <Box width="100%">
            <TextInput
              {...inputProps}
              onBlur={onBlur}
              autoFocus
              plain
              css={`
                padding: 0;
                &:focus {
                  outline: none;
                }
              `}
            />

            {streamName && levelName && (
              <CreateTaskHelpTextBox>
                <Box css="position: absolute; top: 4px;" direction="row">
                  <Text data-testid="task-item-create-input-subtext" size="xsmall" color="text-light" truncate>
                    {t('stream', {
                      streamName,
                      level: levelName
                    })}
                  </Text>
                  <Tooltip content={<ShortcutsContent />} placement="top">
                    <Text
                      size="xsmall"
                      color="text-light"
                      css={`
                        position: relative;
                        top: -3px;
                        left: 4px;
                        color: ${themeColor('primary')};
                        white-space: nowrap;
                      `}
                    >
                      {t('shortcuts')}
                    </Text>
                  </Tooltip>
                </Box>
              </CreateTaskHelpTextBox>
            )}
          </Box>
        }
        endContent={duration ? <Text color="text-light">{formatDuration(duration, 2)}</Text> : null}
        suffixContent={
          avatarSubjects?.length === 0 ? (
            <Avatar />
          ) : avatarSubjects?.length === 1 ? (
            <Avatar subject={avatarSubjects?.[0]} tooltipPlacement="top" />
          ) : (
            <AvatarGroup
              avatars={(avatarSubjects ?? []).map(subject => ({ tooltip: '', subject }))}
              rightPosition={28}
            />
          )
        }
      />
    )
  }
)

const ShortcutsContent = () => {
  const { t } = useLanguage('runbook', { keyPrefix: 'taskList' })

  return (
    <Box gap="small" css="text-align: left; padding: 4px;">
      <Text>{t('shortcutsTitle')}</Text>
      <Text>{t('shortcutsStream')}</Text>
      <Text>{t('shortcutsUserTeam')}</Text>
      <Text>{t('shortcutsTaskType')}</Text>
      <Text>{t('shortcutsDuration')}</Text>
    </Box>
  )
}

const CreateTaskHelpTextBox = styled(Box)`
  position: relative;
  bottom: 7px;
`
